import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { selectIdentity, selectUserInfo } from "../../../selectors/userSelectors";
import Field from "../../../shared/ui/Field/Field";
import Select from "../../../shared/ui/Field/Select";
import TextInput from "../../../shared/ui/Field/TextInput";
import Textarea from "../../../shared/ui/Field/Textarea";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError, parseDrfFieldErrors } from "../../../shared/api/drfErrors";
import {
  fetchCompany,
  fetchMembers,
  fetchMyCompany,
} from "../../companyWorkspace/api/companyWorkspace";
import {
  isAdminMembership,
  isActiveMembership,
  memberDisplayName,
} from "../../companyWorkspace/lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
  sameMembershipGeneration,
} from "../../companyWorkspace/lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../../companyWorkspace/lib/companyFocusRevalidation";
import CompanyPageHeader from "../../companyWorkspace/ui/CompanyPageHeader";
import { fetchCompanyClients } from "../../companyClients/api/companyClients";
import { buildOwnerContactPrefill } from "../../clientPresentation/lib/ownerContactPrefill";
import { buildCompanyManagerContext } from "../../clientPresentation/lib/corporatePresentationMode";
import PresentationManagerModal from "../../clientPresentation/widgets/PresentationManagerModal";
import LandParcelsDetail from "../../landParcels/widgets/LandParcelsDetail";
import {
  getLifecycleConfirmation,
  getOwnerLifecycleActions,
} from "../../realty/lib/propertyLifecycle";
import { COOPERATION_CURRENCY_OPTIONS } from "../../realty/lib/cooperationOffer";
import DetailCooperationBlock from "../../realty/widgets/DetailCooperationBlock";
import DetailDescription from "../../realtyDetail/widgets/DetailDescription";
import DetailFeatures from "../../realtyDetail/widgets/DetailFeatures";
import DetailGallery from "../../realtyDetail/widgets/DetailGallery";
import DetailLocation from "../../realtyDetail/widgets/DetailLocation";
import DetailQuickfacts from "../../realtyDetail/widgets/DetailQuickfacts";
import DetailSpecs from "../../realtyDetail/widgets/DetailSpecs";
import {
  archiveCompanyProperty,
  assignCompanyProperty,
  fetchCompanyProperty,
  fetchCompanyPropertyParticipants,
  publishCompanyProperty,
  putCompanyPropertyCooperation,
  revokeCompanyPropertyParticipant,
  transitionCompanyPropertyStatus,
  unpublishCompanyProperty,
  upsertCompanyPropertyParticipant,
} from "../api/companyProperties";
import { membershipGenerationOf } from "../lib/authorityBoundTransport";
import { createCorporateVideoModalOwner } from "../lib/corporateVideoModalOwner";
import {
  catalogPublicationLabel,
  cooperationFormFromProperty,
  dealTypeLabel,
  formatPropertyPrice,
  hasConfidentialKey,
  hasConfidentialProjection,
  hasRecordKey,
  hasRenderableSubtype,
  isCorporatePropertiesAvailable,
  propertyDisplayTitle,
  propertyStatusLabel,
  propertyTypeLabel,
  toCooperationOfferProperty,
  toGalleryProperty,
  userDisplayName,
} from "../lib/propertyLabels";

const CORPORATE_COOPERATION_DISABLED_NOTE =
  "Пропозиція кооперації вимкнена — партнери її не бачать.";

const COOPERATION_TYPE_OPTIONS = [
  { value: "", label: "Оберіть тип" },
  { value: "percent", label: "Відсоток від ціни угоди" },
  { value: "fixed", label: "Фіксована сума" },
];

function CompanyPropertyDetailContent() {
  const { companyId, propertyId } = useParams();
  const identity = useSelector(selectIdentity);
  const userInfo = useSelector(selectUserInfo);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);
  const membershipGenerationRef = useRef(null);
  const stateRef = useRef("loading");
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  const videoModalOwnerRef = useRef(null);
  if (videoModalOwnerRef.current == null) {
    videoModalOwnerRef.current = createCorporateVideoModalOwner();
  }

  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [property, setProperty] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [state, setState] = useState("loading");
  const [mutationBusy, setMutationBusy] = useState(false);
  const [writesSuspended, setWritesSuspended] = useState(false);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [assignUserId, setAssignUserId] = useState("");
  const [grantDraft, setGrantDraft] = useState({
    user_id: "",
    can_edit: false,
    include_confidential: false,
    expires_at: "",
  });
  const [coopForm, setCoopForm] = useState(null);
  const [coopFieldErrors, setCoopFieldErrors] = useState({});
  const [mediaSessionKey, setMediaSessionKey] = useState(0);
  const [presentationsOpen, setPresentationsOpen] = useState(false);
  const [presentationMembers, setPresentationMembers] = useState([]);
  const [presentationMembersState, setPresentationMembersState] = useState("idle");

  const companyMatches =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const propertyMatches =
    property != null &&
    propertyId != null &&
    String(property.id) === String(propertyId);
  const authorityMatches = companyMatches && actorMatches;
  const canReadInventory =
    authorityMatches && isActiveMembership(company, membership);
  const canUseWrites =
    authorityMatches && isCorporatePropertiesAvailable(company);
  const canManage =
    authorityMatches && isAdminMembership(company, membership);
  const canEdit = Boolean(propertyMatches && property?.can_edit);
  const canMutate = canUseWrites && canEdit;
  const confidentialOk = hasConfidentialProjection(property);
  const showPublicationActions =
    canUseWrites &&
    (property?.can_publish === true || property?.can_unpublish === true);
  const lifecycleActions =
    propertyMatches && canMutate
      ? getOwnerLifecycleActions(property, { isOwner: true }).filter(
          (action) => action.id !== "delete",
        )
      : [];
  const galleryProperty =
    propertyMatches && property ? toGalleryProperty(property) : null;
  const cooperationReadProperty =
    propertyMatches && confidentialOk
      ? toCooperationOfferProperty(property)
      : null;

  const clearProtected = () => {
    setProperty(null);
    setParticipants([]);
    setAssignees([]);
    setActionError(null);
    setAssignUserId("");
    setGrantDraft({
      user_id: "",
      can_edit: false,
      include_confidential: false,
      expires_at: "",
    });
    setCoopForm(null);
    setMediaSessionKey((value) => value + 1);
    setPresentationsOpen(false);
    setPresentationMembers([]);
    setPresentationMembersState("idle");
  };

  const load = useCallback(async ({ background = false } = {}) => {
    if (!companyId || !propertyId) {
      setState("not-found");
      return;
    }
    let activeOp = scopeGate.beginRead({
      companyId,
      actorId,
      resourceId: propertyId,
    });
    const keepVisible = background && stateRef.current === "ready";
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
      setError(null);
    }
    try {
      const [companyData, mine] = await Promise.all([
        fetchCompany(companyId),
        fetchMyCompany(),
      ]);
      if (!scopeGate.readStillValid(activeOp, { mounted: mountedRef.current })) {
        return;
      }
      const mineCompanyMatches =
        mine?.company?.id != null && sameCompanyId(mine.company.id, companyId);
      const mineMatches = mineCompanyMatches ? mine.membership : null;
      setCompany(companyData);
      setMembership(mineMatches);
      setAuthorityActorId(actorId);
      const generation = membershipGenerationOf(mineMatches);
      const priorGeneration = membershipGenerationRef.current;
      membershipGenerationRef.current = generation;
      if (
        keepVisible &&
        priorGeneration != null &&
        !sameMembershipGeneration(priorGeneration, generation)
      ) {
        setMediaSessionKey((value) => value + 1);
      }
      scopeGate.bindIdentity({
        companyId,
        actorId,
        membershipGeneration: generation,
        resourceId: propertyId,
      });

      if (!mineCompanyMatches || !isActiveMembership(companyData, mineMatches)) {
        clearProtected();
        setState("denied");
        return;
      }

      const syncedOp = scopeGate.beginRead({
        companyId,
        actorId,
        resourceId: propertyId,
        membershipGeneration: generation,
      });
      activeOp = syncedOp;
      const detail = await fetchCompanyProperty(companyId, propertyId);
      if (!scopeGate.readStillValid(syncedOp, { mounted: mountedRef.current })) {
        return;
      }
      setProperty(detail);
      setAssignUserId(
        detail.responsible_user?.id != null
          ? String(detail.responsible_user.id)
          : "",
      );
      setCoopForm(cooperationFormFromProperty(detail));

      const manage = isAdminMembership(companyData, mineMatches);
      const writes = isCorporatePropertiesAvailable(companyData);
      if (manage && writes) {
        const [grantList, roster] = await Promise.all([
          fetchCompanyPropertyParticipants(companyId, propertyId),
          fetchMembers(companyId, "active"),
        ]);
        if (!scopeGate.readStillValid(syncedOp, { mounted: mountedRef.current })) {
          return;
        }
        const grants = Array.isArray(grantList)
          ? grantList
          : Array.isArray(grantList?.results)
            ? grantList.results
            : [];
        setParticipants(grants.filter((g) => !g.revoked_at));
        setAssignees(
          (Array.isArray(roster) ? roster : []).filter(
            (m) =>
              m.status === "active" &&
              (m.role === "member" || m.role === "admin"),
          ),
        );
      } else {
        setParticipants([]);
        setAssignees([]);
      }
      revalidationConfirmed = true;
      setActionError((current) =>
        current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
      );
      setState("ready");
    } catch (err) {
      if (!scopeGate.readStillValid(activeOp, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (keepVisible && status !== 404 && status !== 403) {
        setActionError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
        return;
      }
      clearProtected();
      if (status === 404) setState("not-found");
      else if (status === 403) setState("denied");
      else {
        setError(messageFromError(err));
        setState("error");
      }
    } finally {
      settleRevalidationWrites({
        keepVisible: keepVisible,
        confirmed: revalidationConfirmed,
        token: backgroundToken,
        tokenAtStart,
        tokenRef: revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      });
    }
  }, [actorId, companyId, propertyId, scopeGate]);

  useEffect(() => {
    mountedRef.current = true;
    membershipGenerationRef.current = null;
    scopeGate.bindIdentity({
      companyId,
      actorId,
      membershipGeneration: null,
      resourceId: propertyId,
    });
    busyOpRef.current = null;
    clearProtected();
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
      videoModalOwnerRef.current.teardown();
    };
  }, [companyId, actorId, propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bind ownership to the mounted corporate DetailGallery video modal host.
  // Depend on stable scope / media-session lifetime — NOT galleryProperty object
  // identity (toGalleryProperty allocates a fresh object every render and would
  // tear down an open video session on ordinary parent rerenders / form edits).
  const videoModalHostActive =
    state === "ready" && canReadInventory && propertyMatches;
  useEffect(() => {
    const owner = videoModalOwnerRef.current;
    if (!videoModalHostActive) {
      owner.teardown();
      return undefined;
    }
    owner.attach(document.getElementById("detailModalVideo"));
    return () => {
      owner.teardown();
    };
  }, [companyId, actorId, propertyId, mediaSessionKey, videoModalHostActive]);

  useCompanyFocusRevalidation({
    enabled: state === "ready",
    busy: mutationBusy,
    onRevalidate: () => load({ background: true }),
  });

  const runMutation = async (work, onSuccess, options = {}) => {
    if (busyOpRef.current || writesSuspendedRef.current) return;
    const op = scopeGate.beginMutation({
      companyId,
      actorId,
      resourceId: propertyId,
      membershipGeneration: membershipGenerationRef.current,
    });
    busyOpRef.current = op;
    setMutationBusy(true);
    setActionError(null);
    if (options.clearCoopFieldErrors) {
      setCoopFieldErrors({});
    }
    try {
      // Raw HTTP only — success UI side effects run after mutationStillValid.
      await work();
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      if (typeof onSuccess === "function") {
        onSuccess();
      }
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      await load();
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        // R1-2: mutation denial is not automatically inventory READ denial.
        // Drop stale mutation/confidential UI, then let a fresh scoped read
        // decide ready (writes hidden) vs denied / not-found.
        clearProtected();
        setState("loading");
        await load();
        return;
      }
      if (options.fieldErrorKeys && options.fieldErrorKeys.length) {
        const parsed = parseDrfFieldErrors(err, options.fieldErrorKeys);
        const nextFields = { ...parsed };
        const form = nextFields._form || null;
        delete nextFields._form;
        if (Object.keys(nextFields).length) {
          setCoopFieldErrors(nextFields);
          setActionError(form);
          return;
        }
      }
      setActionError(messageFromError(err));
    } finally {
      if (busyOpRef.current === op) {
        busyOpRef.current = null;
      }
      setMutationBusy(false);
    }
  };

  const assigneeOptions = [
    { value: "", label: "Без відповідального" },
    ...assignees.map((m) => ({
      value: String(m.user?.id ?? m.user_id ?? ""),
      label: memberDisplayName(m),
    })),
  ];

  const searchCompanyPresentationClients = useCallback(
    (params = {}) => {
      if (!companyId) return Promise.resolve({ results: [] });
      const search = params.search && String(params.search).trim()
        ? String(params.search).trim()
        : undefined;
      return fetchCompanyClients(companyId, {
        search,
        page: params.page || 1,
        page_size: params.page_size || 20,
      });
    },
    [companyId],
  );

  const openPresentations = async () => {
    if (!canReadInventory || !propertyMatches) return;
    setPresentationsOpen(true);
    if (presentationMembersState === "loaded" || presentationMembersState === "loading") {
      return;
    }
    setPresentationMembersState("loading");
    try {
      const roster = await fetchMembers(companyId, "active");
      if (!mountedRef.current) return;
      const rows = (Array.isArray(roster) ? roster : []).filter(
        (m) => m && m.status === "active" && m.user?.id != null,
      );
      setPresentationMembers(rows);
      setPresentationMembersState("loaded");
    } catch (_err) {
      if (!mountedRef.current) return;
      setPresentationMembers([]);
      setPresentationMembersState("error");
    }
  };

  const generation =
    membershipGenerationRef.current ?? membershipGenerationOf(membership);
  const presentationManagerContext = buildCompanyManagerContext({
    companyId,
    actorMembershipGeneration: generation,
    canCreate: Boolean(property?.can_publish === true && canUseWrites),
    members: presentationMembers.length ? presentationMembers : assignees,
    searchClients: searchCompanyPresentationClients,
  });
  const presentationOwnerPrefill = buildOwnerContactPrefill(userInfo);

  return (
    <div
      className="clients-page feature-company-workspace feature-company-properties"
      id="Csection"
    >
      <div className="container">
        <CompanyPageHeader
          title={
            propertyMatches
              ? propertyDisplayTitle(property)
              : "Об'єкт компанії"
          }
          subtitle="Корпоративний об'єкт · Компанія"
          company={authorityMatches ? company : null}
          membership={authorityMatches ? membership : null}
          companyId={companyId}
          activeNav="properties"
          actions={
            <>
              <Link
                to={`/companies/${companyId}/properties`}
                className="action-btn action-btn--ghost"
              >
                До списку
              </Link>
              <Link
                to={`/companies/${companyId}/tasks?kind=task&property=${propertyId}&create=task`}
                className="btn-more"
              >
                Нова задача
              </Link>
              <Link
                to={`/companies/${companyId}/tasks?kind=viewing&property=${propertyId}&create=viewing`}
                className="action-btn"
              >
                Новий перегляд
              </Link>
              {canMutate && propertyMatches ? (
                <Link
                  to={`/companies/${companyId}/properties/${propertyId}/edit`}
                  className="action-btn"
                >
                  Редагувати
                </Link>
              ) : null}
              {canReadInventory && propertyMatches ? (
                <button
                  type="button"
                  className="action-btn"
                  data-action="client-presentations"
                  data-testid="company-property-presentations"
                  onClick={openPresentations}
                >
                  <i className="fa-solid fa-file-lines" aria-hidden="true" />
                  {" "}
                  Презентації для клієнтів
                </button>
              ) : null}
            </>
          }
        />

        {state === "loading" ? (
          <div className="feature-company-workspace__state">
            <Spinner />
          </div>
        ) : null}

        {state === "denied" ? (
          <EmptyState
            title="Немає доступу"
            description="Немає права переглядати цей корпоративний об'єкт."
          />
        ) : null}

        {state === "not-found" ? (
          <EmptyState
            title="Не знайдено"
            description="Об'єкт компанії не знайдено або приховано."
          />
        ) : null}

        {state === "error" ? (
          <EmptyState
            title="Помилка завантаження"
            description={error || "Спробуйте оновити сторінку."}
            primaryAction={
              <button type="button" className="action-btn" onClick={load}>
                Повторити
              </button>
            }
          />
        ) : null}

        {state === "ready" && canReadInventory && propertyMatches ? (
          <div className="clients-workspace">
            {actionError ? (
              <p className="feature-company-workspace__error" role="alert">
                {actionError}
              </p>
            ) : null}

            <section className="feature-company-workspace__card" aria-label="Огляд">
              <dl className="feature-company-workspace__dl">
                <div>
                  <dt>Тип</dt>
                  <dd>{propertyTypeLabel(property.property_type)}</dd>
                </div>
                <div>
                  <dt>Угода</dt>
                  <dd>{dealTypeLabel(property.deal_type)}</dd>
                </div>
                <div>
                  <dt>Ціна</dt>
                  <dd>{formatPropertyPrice(property)}</dd>
                </div>
                <div>
                  <dt>Робочий статус</dt>
                  <dd>
                    {propertyStatusLabel(
                      property.status || property.work_status,
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Публікація в каталозі</dt>
                  <dd>
                    {catalogPublicationLabel(property.catalog_publication)}
                  </dd>
                </div>
                <div>
                  <dt>Відповідальний</dt>
                  <dd>{userDisplayName(property.responsible_user)}</dd>
                </div>
                {canUseWrites ? (
                  <div>
                    <dt>Редагування</dt>
                    <dd>{property.can_edit ? "Дозволено" : "Лише перегляд"}</dd>
                  </div>
                ) : null}
              </dl>
            </section>

            <div className="feature-company-properties__realty-detail detail-section feature-realty-detail">
              {galleryProperty ? (
                <DetailGallery
                  key={`gallery-${mediaSessionKey}`}
                  property={galleryProperty}
                />
              ) : null}

              <DetailQuickfacts property={property} />

              {hasRenderableSubtype(property) ? (
                <DetailSpecs property={property} />
              ) : null}

              {hasRecordKey(property, "features") ? (
                <DetailFeatures property={property} />
              ) : null}

              <DetailDescription
                property={property}
                canViewPrivateDescription={hasConfidentialKey(
                  property,
                  "description_private",
                )}
              />

              {property.location ? (
                <DetailLocation
                  key={`location-${mediaSessionKey}`}
                  property={property}
                />
              ) : null}

              {hasRecordKey(property, "land_parcels") ? (
                <LandParcelsDetail parcels={property.land_parcels} />
              ) : null}

              {confidentialOk && cooperationReadProperty ? (
                <DetailCooperationBlock
                  property={cooperationReadProperty}
                  disabledNote={CORPORATE_COOPERATION_DISABLED_NOTE}
                />
              ) : null}
            </div>

            {canMutate && confidentialOk && coopForm ? (
              <section
                className="feature-company-workspace__card"
                aria-label="Редагування кооперації"
              >
                <h2>Редагування кооперації</h2>
                <form
                  className="ui-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    runMutation(
                      () =>
                        putCompanyPropertyCooperation(
                          companyId,
                          propertyId,
                          coopForm,
                        ),
                      undefined,
                      {
                        clearCoopFieldErrors: true,
                        fieldErrorKeys: ["cooperation_terms"],
                      },
                    );
                  }}
                >
                  <label className="ui-form__check">
                    <input
                      type="checkbox"
                      checked={Boolean(coopForm.cooperation_offer_enabled)}
                      onChange={(e) =>
                        setCoopForm((prev) => ({
                          ...prev,
                          cooperation_offer_enabled: e.target.checked,
                        }))
                      }
                    />
                    Пропозиція кооперації увімкнена
                  </label>
                  <Field id="coop-type" label="Тип винагороди">
                    <Select
                      id="coop-type"
                      aria-label="Тип винагороди"
                      value={coopForm.cooperation_compensation_type || ""}
                      onChange={(e) => {
                        const nextType = e.target.value;
                        setCoopForm((prev) => ({
                          ...prev,
                          cooperation_compensation_type: nextType,
                          cooperation_compensation_value: "",
                          cooperation_compensation_currency:
                            nextType === "fixed"
                              ? prev.cooperation_compensation_currency ||
                                "USD"
                              : prev.cooperation_compensation_currency ||
                                "USD",
                        }));
                      }}
                      options={COOPERATION_TYPE_OPTIONS}
                    />
                  </Field>
                  <Field id="coop-value" label="Значення">
                    <TextInput
                      id="coop-value"
                      value={coopForm.cooperation_compensation_value ?? ""}
                      onChange={(e) =>
                        setCoopForm((prev) => ({
                          ...prev,
                          cooperation_compensation_value: e.target.value,
                        }))
                      }
                    />
                  </Field>
                  {coopForm.cooperation_compensation_type === "fixed" ? (
                    <Field id="coop-currency" label="Валюта">
                      <Select
                        id="coop-currency"
                        aria-label="Валюта винагороди"
                        value={
                          coopForm.cooperation_compensation_currency || "USD"
                        }
                        onChange={(e) =>
                          setCoopForm((prev) => ({
                            ...prev,
                            cooperation_compensation_currency: e.target.value,
                          }))
                        }
                        options={COOPERATION_CURRENCY_OPTIONS}
                      />
                    </Field>
                  ) : null}
                  <Field
                    id="coop-terms"
                    label="Умови"
                    error={coopFieldErrors.cooperation_terms || null}
                  >
                    <Textarea
                      id="coop-terms"
                      value={coopForm.cooperation_terms || ""}
                      onChange={(e) => {
                        setCoopFieldErrors((prev) => {
                          if (!prev.cooperation_terms) return prev;
                          const next = { ...prev };
                          delete next.cooperation_terms;
                          return next;
                        });
                        setCoopForm((prev) => ({
                          ...prev,
                          cooperation_terms: e.target.value,
                        }));
                      }}
                    />
                  </Field>
                  <button
                    type="submit"
                    className="action-btn action-btn--accent"
                    disabled={Boolean(busyOpRef.current) || writesSuspended}
                  >
                    Зберегти кооперацію
                  </button>
                </form>
              </section>
            ) : null}

            {canMutate ? (
              <section
                className="feature-company-workspace__card"
                aria-label="Робочий статус"
              >
                <h2>Робочий статус</h2>
                <div className="clients-toolbar__row">
                  {lifecycleActions.map((action) => (
                    <button
                      key={action.id}
                      type="button"
                      className={`action-btn ${action.successClass || ""}`.trim()}
                      disabled={Boolean(busyOpRef.current) || writesSuspended}
                      onClick={() => {
                        const confirmation = getLifecycleConfirmation(
                          action.id,
                          property,
                        );
                        const ok = window.confirm(
                          confirmation?.body ||
                            `Змінити статус на «${propertyStatusLabel(action.targetStatus)}»?`,
                        );
                        if (!ok) return;
                        runMutation(() =>
                          transitionCompanyPropertyStatus(
                            companyId,
                            propertyId,
                            action.targetStatus,
                          ),
                        );
                      }}
                    >
                      {action.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="action-btn action-btn--ghost"
                    disabled={Boolean(busyOpRef.current) || writesSuspended}
                    onClick={() => {
                      const ok = window.confirm(
                        "Архівувати об'єкт компанії через корпоративний lifecycle?",
                      );
                      if (!ok) return;
                      runMutation(() =>
                        archiveCompanyProperty(companyId, propertyId),
                      );
                    }}
                  >
                    Архівувати
                  </button>
                </div>
              </section>
            ) : null}

            {showPublicationActions ? (
              <section
                className="feature-company-workspace__card"
                aria-label="Публікація в каталозі"
              >
                <h2>Публікація в каталозі</h2>
                <p className="feature-company-workspace__meta">
                  {catalogPublicationLabel(property.catalog_publication)}
                </p>
                <div className="clients-toolbar__row">
                  {property.can_publish === true ? (
                    <button
                      type="button"
                      className="action-btn"
                      disabled={Boolean(busyOpRef.current) || writesSuspended}
                      onClick={() =>
                        runMutation(
                          () =>
                            publishCompanyProperty(companyId, propertyId),
                          () =>
                            toast.success(
                              "Об'єкт опубліковано в каталозі.",
                            ),
                        )
                      }
                    >
                      Опублікувати в каталозі
                    </button>
                  ) : null}
                  {property.can_unpublish === true ? (
                    <button
                      type="button"
                      className="action-btn action-btn--ghost"
                      disabled={Boolean(busyOpRef.current) || writesSuspended}
                      onClick={() =>
                        runMutation(
                          () =>
                            unpublishCompanyProperty(companyId, propertyId),
                          () =>
                            toast.success("Об'єкт прибрано з каталогу."),
                        )
                      }
                    >
                      Прибрати з каталогу
                    </button>
                  ) : null}
                </div>
              </section>
            ) : null}

            {canManage && canUseWrites ? (
              <section
                className="feature-company-workspace__card"
                aria-label="Призначення"
              >
                <h2>Відповідальний агент</h2>
                <div className="clients-toolbar__row">
                  <Select
                    aria-label="Відповідальний агент"
                    value={assignUserId}
                    onChange={(e) => setAssignUserId(e.target.value)}
                    options={assigneeOptions}
                    disabled={Boolean(busyOpRef.current) || writesSuspended}
                  />
                  <button
                    type="button"
                    className="action-btn action-btn--accent"
                    disabled={Boolean(busyOpRef.current) || writesSuspended}
                    onClick={() =>
                      runMutation(() =>
                        assignCompanyProperty(
                          companyId,
                          propertyId,
                          assignUserId ? Number(assignUserId) : null,
                        ),
                      )
                    }
                  >
                    Зберегти призначення
                  </button>
                  <button
                    type="button"
                    className="action-btn action-btn--ghost"
                    disabled={Boolean(busyOpRef.current) || writesSuspended}
                    onClick={() => {
                      setAssignUserId("");
                      runMutation(() =>
                        assignCompanyProperty(companyId, propertyId, null),
                      );
                    }}
                  >
                    Зняти
                  </button>
                </div>
              </section>
            ) : null}

            {canManage && canUseWrites ? (
              <section
                className="feature-company-workspace__card"
                aria-label="Внутрішні доступи"
              >
                <h2>Внутрішні доступи</h2>
                {participants.length === 0 ? (
                  <p>Немає активних доступів.</p>
                ) : (
                  <ul className="feature-company-workspace__list">
                    {participants.map((grant) => (
                      <li key={grant.id}>
                        <span>
                          {userDisplayName(grant.user)} · редагування:{" "}
                          {grant.can_edit ? "так" : "ні"} · конфіденційно:{" "}
                          {grant.include_confidential ? "так" : "ні"}
                          {grant.expires_at
                            ? ` · до ${grant.expires_at}`
                            : ""}
                        </span>{" "}
                        <button
                          type="button"
                          className="action-btn action-btn--ghost"
                          disabled={Boolean(busyOpRef.current) || writesSuspended}
                          onClick={() => {
                            const ok = window.confirm(
                              "Відкликати внутрішній доступ до цього об'єкта?",
                            );
                            if (!ok) return;
                            runMutation(() =>
                              revokeCompanyPropertyParticipant(
                                companyId,
                                propertyId,
                                grant.id,
                              ),
                            );
                          }}
                        >
                          Відкликати
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <form
                  className="ui-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (!grantDraft.user_id) {
                      setActionError("Оберіть користувача для доступу.");
                      return;
                    }
                    const body = {
                      user_id: Number(grantDraft.user_id),
                      can_edit: Boolean(grantDraft.can_edit),
                      include_confidential: Boolean(
                        grantDraft.include_confidential,
                      ),
                    };
                    if (grantDraft.expires_at) {
                      body.expires_at = grantDraft.expires_at;
                    }
                    runMutation(() =>
                      upsertCompanyPropertyParticipant(
                        companyId,
                        propertyId,
                        body,
                      ),
                    );
                  }}
                >
                  <Field id="grant-user" label="Користувач" required>
                    <Select
                      id="grant-user"
                      value={grantDraft.user_id}
                      onChange={(e) =>
                        setGrantDraft((prev) => ({
                          ...prev,
                          user_id: e.target.value,
                        }))
                      }
                      options={[
                        {
                          value: "",
                          label: "Оберіть учасника або адміністратора",
                        },
                        ...assignees.map((m) => ({
                          value: String(m.user?.id ?? m.user_id ?? ""),
                          label: memberDisplayName(m),
                        })),
                      ]}
                    />
                  </Field>
                  <label className="ui-form__check">
                    <input
                      type="checkbox"
                      checked={Boolean(grantDraft.can_edit)}
                      onChange={(e) =>
                        setGrantDraft((prev) => ({
                          ...prev,
                          can_edit: e.target.checked,
                        }))
                      }
                    />
                    Може редагувати
                  </label>
                  <label className="ui-form__check">
                    <input
                      type="checkbox"
                      checked={Boolean(grantDraft.include_confidential)}
                      onChange={(e) =>
                        setGrantDraft((prev) => ({
                          ...prev,
                          include_confidential: e.target.checked,
                        }))
                      }
                    />
                    Конфіденційні поля
                  </label>
                  <Field id="grant-expires" label="Закінчення (опційно)">
                    <TextInput
                      id="grant-expires"
                      type="datetime-local"
                      value={grantDraft.expires_at}
                      onChange={(e) =>
                        setGrantDraft((prev) => ({
                          ...prev,
                          expires_at: e.target.value,
                        }))
                      }
                    />
                  </Field>
                  <button
                    type="submit"
                    className="action-btn action-btn--accent"
                    disabled={Boolean(busyOpRef.current) || writesSuspended}
                  >
                    Зберегти доступ
                  </button>
                </form>
              </section>
            ) : null}

            {canUseWrites && !canEdit ? (
              <p className="feature-company-workspace__meta">
                Режим лише для читання. Мутації приховані.
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
      {presentationsOpen && propertyMatches && presentationManagerContext ? (
        <PresentationManagerModal
          key={`company-presentation-${property.id}`}
          open
          property={property}
          ownerContactPrefill={presentationOwnerPrefill}
          partnerPriceLocked={false}
          managerContext={presentationManagerContext}
          onClose={() => setPresentationsOpen(false)}
        />
      ) : null}
    </div>
  );
}

export default CompanyPropertyDetailContent;
